import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackMixPlay } from '../hooks/useAnalytics';

const PlayerContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PlayerProvider = ({ children }) => {
  const [currentMix, setCurrentMix] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const audioRef = useRef(null);
  const userWantsToPlay = useRef(false);

  // Initialize audio element once
  useEffect(() => {
    const audio = new Audio();
    // 'metadata' is safer than 'auto' on Android — avoids forcing 100+ MB
    // pre-buffer on cellular which causes playback failure / stalls
    audio.preload = 'metadata';
    audio.volume = volume;
    // iPhone Safari fix - set playsinline attribute
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    audioRef.current = audio;

    // Basic event handlers
    const onTimeUpdate = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsBuffering(false);
    };

    const onPlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
    };

    const onPause = () => {
      // iPhone Safari sometimes pauses randomly - try to resume if user wants to play
      if (userWantsToPlay.current && !audio.ended) {
        console.log('Audio paused unexpectedly, resuming...');
        setTimeout(() => {
          if (userWantsToPlay.current && audio.paused && !audio.ended) {
            audio.play().catch(e => console.log('Resume failed:', e));
          }
        }, 100);
      } else {
        setIsPlaying(false);
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      userWantsToPlay.current = false;
    };

    const onWaiting = () => {
      setIsBuffering(true);
    };

    const onCanPlay = () => {
      setIsBuffering(false);
      // Resume if user wants to play and audio got paused during buffering
      if (userWantsToPlay.current && audio.paused) {
        audio.play().catch(e => console.log('CanPlay resume failed:', e));
      }
    };

    const onError = (e) => {
      console.error('Audio error:', audio.error?.message);
      setIsBuffering(false);
      setIsPlaying(false);
    };

    // iPhone Safari - handle interruptions (phone calls, etc)
    const onStalled = () => {
      console.log('Audio stalled');
      setIsBuffering(true);
    };

    const onSuspend = () => {
      // Safari suspends audio to save power - try to resume
      if (userWantsToPlay.current && audio.paused) {
        console.log('Audio suspended, resuming...');
        audio.play().catch(e => console.log('Suspend resume failed:', e));
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);
    audio.addEventListener('stalled', onStalled);
    audio.addEventListener('suspend', onSuspend);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('stalled', onStalled);
      audio.removeEventListener('suspend', onSuspend);
    };
  }, []);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Handle page visibility - resume when coming back to app
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && userWantsToPlay.current) {
        const audio = audioRef.current;
        if (audio && audio.paused && !audio.ended) {
          console.log('Page visible, resuming audio...');
          audio.play().catch(e => console.log('Visibility resume failed:', e));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Media Session API for lock screen controls (iPhone)
  useEffect(() => {
    if ('mediaSession' in navigator && currentMix) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentMix.title,
        artist: 'DJ Shadow',
        album: currentMix.genre || 'Mix',
        artwork: currentMix.cover_url ? [
          { src: currentMix.cover_url, sizes: '512x512', type: 'image/png' }
        ] : []
      });

      navigator.mediaSession.setActionHandler('play', () => {
        userWantsToPlay.current = true;
        audioRef.current?.play();
      });
      
      navigator.mediaSession.setActionHandler('pause', () => {
        userWantsToPlay.current = false;
        audioRef.current?.pause();
      });

      navigator.mediaSession.setActionHandler('seekbackward', () => {
        if (audioRef.current) {
          audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
        }
      });

      navigator.mediaSession.setActionHandler('seekforward', () => {
        if (audioRef.current) {
          audioRef.current.currentTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 10);
        }
      });
    }
  }, [currentMix]);

  const playMix = async (mix, mixList = []) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (mixList.length > 0) {
      setQueue(mixList);
      const index = mixList.findIndex(m => m.id === mix.id);
      setQueueIndex(index >= 0 ? index : 0);
    }

    setCurrentMix(mix);
    setProgress(0);
    setIsBuffering(true);
    userWantsToPlay.current = true; // User wants to play

    // Get audio URL (build synchronously to preserve user gesture on Android Chrome)
    let audioUrl;
    if (mix.audio_url.startsWith('http')) {
      audioUrl = mix.audio_url;
    } else {
      audioUrl = `${BACKEND_URL}${mix.audio_url}`;
    }

    console.log('Playing:', mix.title, audioUrl);

    // Stop current playback and load new source — must run synchronously
    // from the user-gesture handler so Android Chrome doesn't block play()
    audio.pause();
    audio.currentTime = 0;
    audio.src = audioUrl;

    // Kick off play() immediately while user gesture is still valid.
    // Do NOT await any network call before this point.
    const playPromise = audio.play();

    // Fire-and-forget analytics AFTER play() has been initiated.
    // These no longer block the user-gesture flow.
    trackMixPlay(mix.id, mix.title);
    axios.post(`${API_URL}/mixes/${mix.id}/play`).catch((error) => {
      console.error('Failed to record play:', error);
    });

    try {
      await playPromise;
      setIsPlaying(true);
    } catch (error) {
      console.error('Play failed:', error);
      setIsBuffering(false);
    }
  };

  const playNext = useCallback(() => {
    if (queue.length > 0 && queueIndex < queue.length - 1) {
      const nextIndex = queueIndex + 1;
      setQueueIndex(nextIndex);
      playMix(queue[nextIndex], queue);
    }
  }, [queue, queueIndex]);

  const playPrevious = useCallback(() => {
    if (queue.length > 0 && queueIndex > 0) {
      const prevIndex = queueIndex - 1;
      setQueueIndex(prevIndex);
      playMix(queue[prevIndex], queue);
    }
  }, [queue, queueIndex]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !currentMix) return;
    
    if (isPlaying) {
      userWantsToPlay.current = false;
      audio.pause();
      setIsPlaying(false);
    } else {
      userWantsToPlay.current = true;
      audio.play().catch(err => {
        console.error('Toggle play failed:', err);
      });
    }
  };

  const seekTo = (newProgress) => {
    const audio = audioRef.current;
    if (audio && audio.duration) {
      audio.currentTime = (newProgress / 100) * audio.duration;
      setProgress(newProgress);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentTime = () => {
    return formatTime(audioRef.current?.currentTime || 0);
  };

  const getDuration = () => {
    return formatTime(duration);
  };

  return (
    <PlayerContext.Provider value={{
      currentMix,
      isPlaying,
      isBuffering,
      progress,
      volume,
      queue,
      queueIndex,
      playMix,
      togglePlay,
      playNext,
      playPrevious,
      seekTo,
      setVolume,
      getCurrentTime,
      getDuration,
      formatTime,
      duration
    }}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};
